import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { format, parseISO } from "date-fns";
import axios from "axios";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Typography,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import { NavLink } from "react-router-dom";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";

import Table from "../../../components/Table";
import Panel from "../../../components/panels/Panel";
import { Helmet } from "react-helmet-async";
import { useColumnLookups } from "../../../hooks/useColumnLookups";

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  max-height: calc(100% - 48px);
`;

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

// Fetch and transform data
const useMostRecentData = () => {
  return useQuery(
    ["/api/most-recently-collected"],
    async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/most-recently-collected`
      );

      // Convert ISO date strings to Date objects for proper filtering
      return data.map((row) => ({
        ...row,
        collect_date: row.collect_date ? parseISO(row.collect_date) : null,
      }));
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
};

const MostRecentData = () => {
  const { data, isFetching, error } = useMostRecentData();

  // Generate lookup options for filtering
  const lookups = useColumnLookups(data, ["organization_name"]);

  // Memoize column definitions for performance
  const columns = useMemo(
    () => [
      {
        title: "Location Name",
        field: "location_name",
        cellStyle: {
          minWidth: 500,
        },
        filterCellStyle: {
          maxWidth: 500,
        },
      },
      {
        title: "Parameter",
        field: "parameter",
        cellStyle: {
          minWidth: 400,
        },
      },
      {
        title: "Organization Name",
        field: "organization_name",
        lookup: lookups.organization_name,
        cellStyle: {
          minWidth: 300,
        },
      },
      {
        title: "Collection Date",
        field: "collect_date",
        type: "date",
        render: (rowData) => {
          if (!rowData.collect_date) return "N/A";
          return format(rowData.collect_date, "MM/dd/yyyy");
        },
        cellStyle: {
          minWidth: 175,
        },
      },
    ],
    [lookups.organization_name]
  );

  if (error) return "An error has occurred: " + error.message;

  return (
    <>
      <Helmet title="Most Recent Data" />
      <Typography variant="h3" gutterBottom display="inline">
        Most Recent Data
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/">
          Dashboard
        </Link>
        <Typography>Most Recent Data</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Panel>
        <TableWrapper>
          <Table
            label="Most Recent Data"
            isLoading={isFetching}
            columns={columns}
            data={data}
            height="calc(100vh - 64px - 48px - 106px - 48px - 64px - 156px)"
            pageSize={60}
            filtering
          />
        </TableWrapper>
      </Panel>
    </>
  );
};

export default MostRecentData;
